"use client";

import React, { useState } from "react";
import { ShieldCheck, Lock, Activity, CircleCheck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const mockShieldAnim = {
  v: "5.7.0",
  fr: 60,
  ip: 0,
  op: 60,
  w: 100,
  h: 100,
  nm: "Shield Mock",
  ddd: 0,
  assets: [],
  layers: [{
    ddd: 0,
    ind: 1,
    ty: 4,
    nm: "Shape Layer 1",
    sr: 1,
    ks: {
      o: {
        a: 0,
        k: 100,
        ix: 11
      },
      r: {
        a: 1,
        k: [{
          i: {
            x: [0.833],
            y: [0.833]
          },
          o: {
            x: [0.167],
            y: [0.167]
          },
          t: 0,
          s: [0]
        }, {
          t: 60,
          s: [360]
        }],
        ix: 10
      },
      p: {
        a: 0,
        k: [50, 50, 0],
        ix: 2
      },
      a: {
        a: 0,
        k: [0, 0, 0],
        ix: 1
      },
      s: {
        a: 0,
        k: [100, 100, 100],
        ix: 6
      }
    },
    ao: 0,
    shapes: [{
      ty: "el",
      d: 1,
      p: {
        a: 0,
        k: [0, 0],
        ix: 3
      },
      s: {
        a: 0,
        k: [50, 50],
        ix: 2
      },
      nm: "Ellipse Path 1",
      mn: "ADBE Vector Shape - Ellipse",
      hd: false
    }, {
      ty: "st",
      c: {
        a: 0,
        k: [0, 0.8, 1, 1],
        ix: 3
      },
      o: {
        a: 0,
        k: 100,
        ix: 4
      },
      w: {
        a: 0,
        k: 4,
        ix: 5
      },
      lc: 1,
      lj: 1,
      ml: 4,
      bm: 0,
      nm: "Stroke 1",
      mn: "ADBE Vector Graphic - Stroke",
      hd: false
    }],
    ip: 0,
    op: 60,
    st: 0,
    bm: 0
  }]
};
const features = [{
  id: "permissions",
  title: "Granular Permissions",
  description: "Scope every action with process-level isolation and strict file-system sandboxing protocols for local security.",
  icon: ShieldCheck,
  animation: mockShieldAnim,
  specs: ["Process-Level Isolation", "Scoped File Access", "Network Sandboxing", "Identity-Linked Keys"]
}, {
  id: "memory",
  title: "Encrypted Memory",
  description: "Your agent's long-term memory is stored locally with hardware-backed encryption and zero-knowledge architecture.",
  icon: Lock,
  animation: mockShieldAnim,
  specs: ["Zero-Knowledge Storage", "RAM-Only Execution", "Hardware-Backed Keys", "Immutable Audit Logs"]
}, {
  id: "approvals",
  title: "Human-in-the-loop",
  description: "Critical workflows require explicit iMessage approval before execution, ensuring total operator sovereignty.",
  icon: Activity,
  animation: mockShieldAnim,
  specs: ["Real-time Approvals", "Multi-Factor Verification", "Manual Override Hooks", "Operator Transparency"]
}];
export default function OurDigitalFortress() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="55383" id="security-permissions" ref={ref} className="dark bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:20px_20px]"></div>
        <div className="absolute left-0 right-0 top-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-primary/10 opacity-15 blur-[120px]"></div>
        <div className="absolute right-10 bottom-10 -z-10 h-[200px] w-[200px] rounded-full bg-blue-900/10 opacity-15 blur-[100px]"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className={`mx-auto max-w-2xl text-center transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-primary font-mono text-xs font-semibold tracking-widest uppercase mb-4">
            Sovereign Agent Protocol
          </h2>
          <h3 className="text-4xl sm:text-6xl font-bold tracking-tighter text-foreground mb-8 font-sans">
            Security by Sovereignty
          </h3>
          <p className="text-muted-foreground text-lg sm:text-xl max-w-prose mx-auto mb-16 leading-relaxed">
            qOS operates as a private, local-first macOS environment. Your data never leaves your hardware without explicit, human-in-the-loop approval.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {features.map((feature, index) => <SecurityCard key={feature.id} feature={feature} index={index} isVisible={isVisible} />)}
        </div>

        {}
        <div className={`mt-20 text-center transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Link to="/QOSByQuotientPage">
            <Button size="lg" className="group bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-10 h-14 rounded-lg shadow-[0_0_25px_rgba(var(--primary),0.25)] transition-all">
              Deploy Your Instance
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}
function SecurityCard({
  feature,
  index,
  isVisible
}: {
  feature: typeof features[0];
  index: number;
  isVisible: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return <div data-index={index} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className={`
          relative h-full overflow-hidden border transition-all duration-500
          bg-card/10 backdrop-blur-xl
          ${isHovered ? "border-primary/50 shadow-[0_0_40px_-10px_hsl(var(--primary)/0.4)]" : "border-white/5 shadow-none"}
        `} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        {}
        <div className={`absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent transition-opacity duration-500 pointer-events-none ${isHovered ? "opacity-100" : "opacity-0"}`} />

        <CardHeader className="relative z-10 pb-4">
          <div className="mb-6 flex items-center justify-between">
            {}
            <div className="relative h-14 w-14 flex items-center justify-center rounded-xl bg-background/60 border border-white/10 shadow-inner">
              <feature.icon className={`h-7 w-7 transition-colors duration-300 ${isHovered ? "text-primary" : "text-muted-foreground/70"}`} />
            </div>
            <div className="font-mono text-[10px] text-muted-foreground/40 tracking-[0.2em] uppercase rotate-90 origin-top-right translate-x-3">
              SYS_LOG_0{index + 1}
            </div>
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight font-sans text-foreground">
            {feature.title}
          </CardTitle>
        </CardHeader>

        <CardContent className="relative z-10 space-y-8">
          <CardDescription className="text-base text-muted-foreground/90 leading-relaxed">
            {feature.description}
          </CardDescription>
          
          <div className="space-y-4">
            <div className="h-px w-full bg-gradient-to-r from-transparent via-primary/20 to-transparent opacity-40" />
            <ul className="space-y-3">
              {feature.specs.map((spec, i) => <li key={i} data-index={i} className="flex items-center gap-3 text-sm font-mono text-muted-foreground/70">
                  <CircleCheck className={`h-4 w-4 flex-shrink-0 transition-colors duration-300 ${isHovered ? "text-primary" : "text-muted-foreground/30"}`} />
                  <span className={isHovered ? "text-foreground/90 transition-colors duration-300" : "transition-colors duration-300"}>
                    {spec}
                  </span>
                </li>)}
            </ul>
          </div>
        </CardContent>

        <CardFooter className="relative z-10 pt-4">
           <div className={`text-[10px] font-mono tracking-widest transition-colors duration-300 ${isHovered ? "text-primary" : "text-muted-foreground/30"}`}>
              STATUS: ENFORCED
           </div>
        </CardFooter>
      </Card>
    </div>;
}